/* Roast scroller.

   This does three things the CSS cannot.

   It makes the shelf grabbable. Pointer events move the bags with the cursor,
   one to one, so they slide across the screen instead of stepping between
   positions. Releasing mid-flick hands the remaining speed to a friction glide,
   which is what makes it feel like a spun dial rather than a control.

   It sizes the bags. Scale is a function of distance from center, recalculated
   every frame, so the presented bag grows smoothly as it travels rather than
   popping when it arrives. The previous version left this to a CSS scroll
   timeline, which is correct in principle but only animates against real scroll
   position and could not follow a JS-driven glide.

   And it keeps the range at the bottom and the shelf agreeing with each other
   without the two chasing each other in a loop.

   There is deliberately no snapping. An earlier build used mandatory scroll
   snap and it was the single biggest reason the component felt mechanical. */
(function () {
  'use strict';

  var FRICTION = 0.945;   // per frame decay of a flick
  var SETTLE = 0.055;     // pull toward the nearest bag once the flick dies
  var SETTLE_DAMP = 0.80;
  var MAX_FLICK = 110;    // px/frame, so a violent throw stays readable
  var DRAG_SLOP = 6;      // px of movement before a click counts as a drag

  var reduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setup(root) {
    var track = root.querySelector('[data-roast-track]');
    var range = root.querySelector('[data-roast-range]');
    var prev = root.querySelector('[data-roast-prev]');
    var next = root.querySelector('[data-roast-next]');
    if (!track) return;

    var items = Array.prototype.slice.call(track.children);
    if (!items.length) return;

    var focus = 1.5;
    var resting = 0.58;
    var cs = getComputedStyle(root);
    focus = parseFloat(cs.getPropertyValue('--focus')) || focus;
    resting = parseFloat(cs.getPropertyValue('--resting')) || resting;

    var pressed = false;
    var dragging = false;
    var wasDrag = false;
    var moved = 0;
    var vel = 0;
    var raf = null;
    var settling = false;
    var target = 0;
    var startX = 0;
    var startScroll = 0;
    var lastX = 0;
    var lastT = 0;
    var syncing = false;

    function maxScroll() {
      return Math.max(1, track.scrollWidth - track.clientWidth);
    }

    // Scroll position that puts item i dead center.
    function centreOf(i) {
      var el = items[i];
      return el.offsetLeft + el.offsetWidth / 2 -
        track.offsetLeft - track.clientWidth / 2;
    }

    function nearest() {
      var best = 0;
      var bd = Infinity;
      for (var i = 0; i < items.length; i++) {
        var d = Math.abs(centreOf(i) - track.scrollLeft);
        if (d < bd) { bd = d; best = i; }
      }
      return best;
    }

    /* Distance from center drives scale, so a bag grows on its way in rather
       than switching size on arrival. One bag-width away is fully small; the
       curve is eased so the middle of the shelf stays calm and the change
       happens close to the center.

       The transform goes on the bag, not the list item, so the name and price
       below it hold one size and one position while the artwork moves. */
    function paint() {
      if (reduced) return;
      var mid = track.scrollLeft + track.clientWidth / 2;
      var span = items[0].offsetWidth * 1.35;
      var centered = nearest();
      for (var i = 0; i < items.length; i++) {
        var el = items[i];
        var bag = el.querySelector('.roast-scroller__bag');
        var c = el.offsetLeft - track.offsetLeft + el.offsetWidth / 2;
        var t = Math.min(1, Math.abs(c - mid) / span);
        var ease = 1 - t * t * (3 - 2 * t);           // smoothstep, 1 at center
        var s = resting + (focus - resting) * ease;
        if (bag) {
          bag.style.transform = 'scale(' + s.toFixed(4) + ')';
          bag.style.opacity = (0.4 + 0.6 * ease).toFixed(3);
        }
        el.style.zIndex = String(100 + Math.round(ease * 100));
        el.classList.toggle('is-centered', i === centered);
      }
    }

    function syncRange() {
      if (!range || syncing) return;
      syncing = true;
      range.value = String(Math.round((track.scrollLeft / maxScroll()) * 1000));
      syncing = false;
    }

    function updateArrows() {
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll() - 2;
    }

    function frameUpdate() {
      paint();
      syncRange();
      updateArrows();
    }

    function stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      settling = false;
    }

    function glide() {
      stop();
      function step() {
        var max = maxScroll();
        if (!settling) {
          vel *= FRICTION;
          track.scrollLeft += vel;
          // Hitting an end kills the throw rather than pinning it there.
          if (track.scrollLeft <= 0 || track.scrollLeft >= max) vel *= 0.35;
          if (Math.abs(vel) < 0.7) { settling = true; target = centreOf(nearest()); }
        } else {
          var d = target - track.scrollLeft;
          vel = (vel + d * SETTLE) * SETTLE_DAMP;
          track.scrollLeft += vel;
          if (Math.abs(d) < 0.4 && Math.abs(vel) < 0.4) {
            track.scrollLeft = target;
            frameUpdate();
            stop();
            return;
          }
        }
        frameUpdate();
        raf = requestAnimationFrame(step);
      }
      raf = requestAnimationFrame(step);
    }

    function springTo(i) {
      stop();
      vel = 0;
      settling = true;
      target = centreOf(Math.max(0, Math.min(items.length - 1, i)));
      glide();
    }

    /* A press is not yet a drag.

       Both the is-dragging class and pointer capture used to be applied here,
       on pointerdown, and together they made a bag impossible to open. The
       class sets pointer-events:none on the anchor, so the anchor was not
       hit-testable at either mousedown or mouseup and the browser resolved the
       click onto the list item instead; capture then retargeted what was left
       to the track. A perfectly still click never reached the link.

       Neither belongs on the press. The drag starts on the first real movement
       past the slop, so a click is an ordinary click on an ordinary anchor,
       keeping middle-click, ctrl-click and open-in-new-tab intact for free. */
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      stop();
      pressed = true;
      dragging = false;
      wasDrag = false;
      moved = 0;
      vel = 0;
      startX = lastX = e.clientX;
      startScroll = track.scrollLeft;
      lastT = e.timeStamp;
    });

    track.addEventListener('pointermove', function (e) {
      if (!pressed) return;

      if (!dragging) {
        // Net travel from where the finger landed, not distance accumulated
        // sample by sample: a slow, shaky click racks up absolute movement
        // without ever going anywhere.
        if (Math.abs(e.clientX - startX) <= DRAG_SLOP) return;
        dragging = true;
        wasDrag = true;
        root.classList.add('is-dragging');
        try { track.setPointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
      }

      var dx = e.clientX - lastX;
      var dt = Math.max(1, e.timeStamp - lastT);
      moved += Math.abs(dx);
      track.scrollLeft = startScroll - (e.clientX - startX);
      // Smoothed so one jittery sample cannot define the throw.
      vel = vel * 0.68 + (-dx * (16.7 / dt)) * 0.32;
      lastX = e.clientX;
      lastT = e.timeStamp;
      frameUpdate();
      e.preventDefault();
    });

    function release(e) {
      if (!pressed) return;
      pressed = false;
      if (!dragging) return;          // a plain click: leave it to the anchor
      dragging = false;
      root.classList.remove('is-dragging');
      try { track.releasePointerCapture(e.pointerId); } catch (err) { /* fine */ }
      vel = Math.max(-MAX_FLICK, Math.min(MAX_FLICK, vel));
      glide();
    }
    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);

    /* A drag that ends on a bag must not also open that bag. This keys off the
       gesture that just happened rather than off a lingering `moved` total,
       which stayed above the slop after any drag and went on to swallow the
       next Enter press on a focused bag. detail is 0 for keyboard activation,
       which is never a drag. */
    track.addEventListener('click', function (e) {
      if (wasDrag && e.detail !== 0) { e.preventDefault(); e.stopPropagation(); }
      wasDrag = false;
    }, true);

    // Wheel and trackpad still scroll natively; settle once they stop.
    var idle = null;
    track.addEventListener('scroll', function () {
      if (dragging || raf) return;
      frameUpdate();
      clearTimeout(idle);
      idle = setTimeout(function () {
        if (dragging || raf) return;
        vel = 0;
        settling = true;
        target = centreOf(nearest());
        glide();
      }, 110);
    }, { passive: true });

    if (range) {
      range.addEventListener('pointerdown', stop);
      range.addEventListener('input', function () {
        if (syncing) return;
        stop();
        track.scrollLeft = (Number(range.value) / 1000) * maxScroll();
        paint();
        updateArrows();
      });
      range.addEventListener('change', function () {
        vel = 0;
        settling = true;
        target = centreOf(nearest());
        glide();
      });
    }

    if (prev) prev.addEventListener('click', function () { springTo(nearest() - 1); });
    if (next) next.addEventListener('click', function () { springTo(nearest() + 1); });

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); springTo(nearest() + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); springTo(nearest() - 1); }
    });

    if (window.ResizeObserver) {
      new ResizeObserver(function () { frameUpdate(); }).observe(track);
    }

    // Images arrive after first paint and change the geometry the scale maths
    // depends on, so recompute as they land.
    Array.prototype.forEach.call(track.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', frameUpdate, { once: true });
    });

    frameUpdate();
  }

  function init() {
    var roots = document.querySelectorAll('.roast-scroller');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
